import React, { useEffect, useState } from 'react';

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ModeIcon from '@mui/icons-material/Mode';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  ListItemText,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import InclusionAssetEditDialog, { type InclusionAssetEditValue } from './InclusionAssetEditDialog';
import {
  type MasterInclusion,
  type MasterInclusionEdit,
  type PackageOption,
  TABLE_GRID_COLUMNS,
  clearPackageOptions,
  filterPackageOptions,
  selectPackageOptions,
  toMasterInclusionEdit,
} from './utils';

interface Props {
  inclusion: MasterInclusion;
  packageOptions: Array<PackageOption>;
  onChange: (id: string, nextValues: Partial<MasterInclusion>) => void;
  onSaveEdit: (inclusion: MasterInclusion, nextValues: MasterInclusionEdit) => Promise<void>;
  onUnassign: (inclusion: MasterInclusion) => Promise<void>;
}

const ROW_TEXT_SX = {
  display: '-webkit-box',
  overflow: 'hidden',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 2,
} as const;

function getAssignedPackagesLabel(selectedPackageIds: Array<string>, packageOptions: Array<PackageOption>) {
  const packageOptionIds = new Set(packageOptions.map((option) => option.id));
  const validPackageIds = selectedPackageIds.filter((packageId) => packageOptionIds.has(packageId));

  if (packageOptions.length === 0) {
    return 'No packages available';
  }

  if (validPackageIds.length === 0) {
    return 'No packages';
  }

  if (validPackageIds.length === packageOptions.length) {
    return 'All packages';
  }

  if (validPackageIds.length === 1) {
    return packageOptions.find((option) => option.id === validPackageIds[0])?.name ?? '1 package';
  }

  return `${validPackageIds.length} of ${packageOptions.length} packages`;
}

function getDraftInclusion(inclusion: MasterInclusion): InclusionAssetEditValue {
  return {
    category: inclusion.category,
    categoryId: inclusion.categoryId ?? null,
    currency: inclusion.currency ?? '',
    isHidden: !!inclusion.isHidden,
    name: inclusion.inclusion,
    requiresBooking: !!inclusion.requiresBooking,
    rrp: inclusion.rrp ?? 0,
    type: inclusion.type ?? 'Flash',
    unit: inclusion.unit ?? '',
  };
}

function buildMasterInclusionEdit(value: InclusionAssetEditValue): MasterInclusionEdit {
  return {
    category: value.category,
    categoryId: value.categoryId,
    currency: value.currency,
    inclusion: value.name,
    isHidden: value.isHidden,
    requiresBooking: value.requiresBooking,
    rrp: value.rrp,
    type: value.type,
    unit: value.unit,
  };
}

export default function InclusionRow({ inclusion, packageOptions, onChange, onSaveEdit, onUnassign }: Props) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: inclusion.id });
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isPackageDialogOpen, setIsPackageDialogOpen] = useState(false);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [isSavingRequiresBooking, setIsSavingRequiresBooking] = useState(false);
  const [packageSearch, setPackageSearch] = useState('');
  const [draftInclusion, setDraftInclusion] = useState<InclusionAssetEditValue>(() => getDraftInclusion(inclusion));
  const visiblePackageOptions = filterPackageOptions(packageOptions, packageSearch);
  const hasUnselectedVisiblePackages = visiblePackageOptions.some(
    (option) => !inclusion.assignedPackageIds.includes(option.id),
  );
  const hasSelectedVisiblePackages = visiblePackageOptions.some((option) =>
    inclusion.assignedPackageIds.includes(option.id),
  );
  const assignedPackagesLabel = getAssignedPackagesLabel(inclusion.assignedPackageIds, packageOptions);
  useEffect(() => {
    if (!isEditDialogOpen) {
      setDraftInclusion(getDraftInclusion(inclusion));
    }
  }, [inclusion, isEditDialogOpen]);

  const closePackageDialog = () => {
    setIsPackageDialogOpen(false);
    setPackageSearch('');
  };

  const togglePackageAssignment = (packageId: string) => {
    const assignedPackageIds = inclusion.assignedPackageIds.includes(packageId)
      ? inclusion.assignedPackageIds.filter((assignedPackageId) => assignedPackageId !== packageId)
      : inclusion.assignedPackageIds.concat(packageId);

    onChange(inclusion.id, { assignedPackageIds });
  };

  const selectAllPackages = () => {
    onChange(inclusion.id, {
      assignedPackageIds: selectPackageOptions(inclusion.assignedPackageIds, visiblePackageOptions),
    });
  };

  const clearPackages = () => {
    onChange(inclusion.id, {
      assignedPackageIds: clearPackageOptions(inclusion.assignedPackageIds, visiblePackageOptions),
    });
  };

  const handleCloseEditDialog = () => {
    setDraftInclusion(getDraftInclusion(inclusion));
    setIsEditDialogOpen(false);
  };

  const handleSaveEditDialog = async () => {
    setIsSavingEdit(true);

    try {
      await onSaveEdit(inclusion, buildMasterInclusionEdit(draftInclusion));
      setIsEditDialogOpen(false);
    } catch {
      return;
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleToggleRequiresBooking = async (checked: boolean) => {
    setIsSavingRequiresBooking(true);

    try {
      await onSaveEdit(inclusion, toMasterInclusionEdit(inclusion, { requiresBooking: checked }));
    } catch {
      // Error feedback is handled by onSaveEdit's caller.
    } finally {
      setIsSavingRequiresBooking(false);
    }
  };

  const handleUnassignEditDialog = async () => {
    setIsSavingEdit(true);

    try {
      await onUnassign(inclusion);
      setIsEditDialogOpen(false);
    } catch {
      return;
    } finally {
      setIsSavingEdit(false);
    }
  };

  return (
    <>
      <Box
        ref={setNodeRef}
        sx={{
          alignItems: 'center',
          bgcolor: 'background.paper',
          borderTop: 1,
          borderColor: 'divider',
          display: 'grid',
          gap: 2,
          gridTemplateColumns: TABLE_GRID_COLUMNS,
          minHeight: 80,
          opacity: isDragging ? 0.7 : 1,
          px: 2,
          py: 2,
          position: 'relative',
          transform: CSS.Transform.toString(transform),
          transition,
          zIndex: isDragging ? 1 : 'auto',
          '& .MuiInputBase-root': {
            bgcolor: 'background.paper',
          },
        }}
      >
        <IconButton
          size="small"
          aria-label={`Reorder ${inclusion.inclusion || 'inclusion'}`}
          sx={{ color: 'text.disabled', cursor: 'grab' }}
          onClick={(event) => event.stopPropagation()}
          {...attributes}
          {...listeners}
        >
          <DragIndicatorIcon fontSize="small" />
        </IconButton>

        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={ROW_TEXT_SX}>
            {inclusion.category}
          </Typography>
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={ROW_TEXT_SX}>
            {inclusion.inclusion}
          </Typography>
        </Box>

        <Stack direction="row" justifyContent="center">
          <Switch
            size="small"
            checked={inclusion.highlighted}
            onChange={(event) =>
              onChange(inclusion.id, {
                highlighted: event.target.checked,
                luxPlusOnly: event.target.checked ? false : inclusion.luxPlusOnly,
              })
            }
            disabled={inclusion.luxPlusOnly}
          />
        </Stack>

        <Stack direction="row" justifyContent="center">
          <Switch
            size="small"
            checked={inclusion.luxPlusOnly}
            onChange={(event) =>
              onChange(inclusion.id, {
                highlighted: event.target.checked ? false : inclusion.highlighted,
                luxPlusOnly: event.target.checked,
              })
            }
            disabled={inclusion.highlighted}
          />
        </Stack>

        <Stack direction="row" justifyContent="center">
          <Switch
            size="small"
            checked={!!inclusion.requiresBooking}
            disabled={isSavingRequiresBooking}
            onChange={(event) => void handleToggleRequiresBooking(event.target.checked)}
          />
        </Stack>

        <Box>
          <Button
            fullWidth
            variant="outlined"
            color="primary"
            disabled={packageOptions.length === 0}
            onClick={() => setIsPackageDialogOpen(true)}
            sx={{ justifyContent: 'flex-start', minHeight: 40, textAlign: 'left', textTransform: 'none' }}
          >
            {assignedPackagesLabel}
          </Button>
          <Dialog open={isPackageDialogOpen} onClose={closePackageDialog} fullWidth maxWidth="sm">
            <DialogTitle>Assigned packages</DialogTitle>
            <DialogContent dividers sx={{ p: 0 }}>
              <Stack
                spacing={0.5}
                sx={{ bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider', px: 2, pt: 2, pb: 0.5 }}
              >
                <TextField
                  autoFocus
                  fullWidth
                  size="small"
                  placeholder="Search packages"
                  value={packageSearch}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    },
                  }}
                  onChange={(event) => setPackageSearch(event.target.value)}
                />
                <Stack direction="row" justifyContent="flex-end" spacing={1}>
                  <Button
                    variant="text"
                    size="small"
                    disabled={!hasUnselectedVisiblePackages}
                    onClick={selectAllPackages}
                    sx={{ minHeight: 28 }}
                  >
                    Select all
                  </Button>
                  <Button
                    variant="text"
                    size="small"
                    disabled={!hasSelectedVisiblePackages}
                    onClick={clearPackages}
                    sx={{ minHeight: 28 }}
                  >
                    Clear
                  </Button>
                </Stack>
              </Stack>
              <Box sx={{ maxHeight: 420, overflowY: 'auto', py: 0.5 }}>
                {visiblePackageOptions.map((option) => (
                  <MenuItem key={option.id} onClick={() => togglePackageAssignment(option.id)} sx={{ gap: 1, py: 1 }}>
                    <Checkbox
                      size="small"
                      checked={inclusion.assignedPackageIds.includes(option.id)}
                      sx={{ p: 0.25 }}
                    />
                    <ListItemText primary={option.name} />
                  </MenuItem>
                ))}
                {visiblePackageOptions.length === 0 && (
                  <MenuItem disabled>
                    <ListItemText primary="No packages found" />
                  </MenuItem>
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button variant="text" color="primary" onClick={closePackageDialog}>
                Done
              </Button>
            </DialogActions>
          </Dialog>
        </Box>

        <IconButton color="primary" aria-label="Edit inclusion" onClick={() => setIsEditDialogOpen(true)}>
          <ModeIcon />
        </IconButton>
      </Box>

      <InclusionAssetEditDialog
        isCustom={inclusion.isCustom}
        isSaving={isSavingEdit}
        open={isEditDialogOpen}
        value={draftInclusion}
        onChange={setDraftInclusion}
        onClose={handleCloseEditDialog}
        onSave={() => void handleSaveEditDialog()}
        onUnassign={handleUnassignEditDialog}
        unassignActionLabel="Unassign all"
        unassignConfirmationText={`Unassign ${inclusion.inclusion || 'this inclusion'} from all offer packages?`}
      />
    </>
  );
}
